import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HeaderBar from '../components/HeaderBar';
import './UnitAdminPage.css';

function UnitAdminPage({ user, onLogout }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Initialize tab from URL query parameter or default to 'dashboard'
    const initialTab = searchParams.get('tab') || 'dashboard';
    const [activeTab, setActiveTab] = useState(initialTab); // dashboard, manage-desks, book-desk, manage-bookings

    // Dashboard data
    const [dashboardData, setDashboardData] = useState({
        unit: '',
        users: [],
        desks: [],
    });

    // Analytics
    const [analytics, setAnalytics] = useState({
        totalDesks: 0,
        occupiedDesks: 0,
        occupancyRate: 0,
        totalUsers: 0,
        activeBookings: 0,
    });

    // Manage desks state
    const [deskList, setDeskList] = useState([]);
    const [selectedDesk, setSelectedDesk] = useState(null);
    const [showDeskModal, setShowDeskModal] = useState(false);
    const [deskAction, setDeskAction] = useState(null); // 'reassign', 'flex-pool', 'delete'

    // Book desk state
    const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedDeskToBook, setSelectedDeskToBook] = useState('');
    const [availableDesks, setAvailableDesks] = useState([]);

    // Manage bookings state
    const [bookingsList, setBookingsList] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [bookingAction, setBookingAction] = useState(null); // 'cancel', 'reassign'
    const [reassignDeskId, setReassignDeskId] = useState('');

    // Load initial data
    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/unit-admin/dashboard');
            setDashboardData(response.data);
            calculateAnalytics(response.data);
            setDeskList(response.data.desks || []);
            fetchBookings();
        } catch (err) {
            setError('Failed to load unit admin dashboard');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const calculateAnalytics = (data) => {
        const desks = data.desks || [];
        const users = data.users || [];

        setAnalytics({
            totalDesks: desks.length,
            occupiedDesks: 0,
            occupancyRate: 0,
            totalUsers: users.length,
            activeBookings: 0,
        });
    };

    const fetchBookings = async () => {
        try {
            const response = await axios.get('/api/unit-admin/bookings');
            const bookings = response.data || [];
            setBookingsList(bookings);

            const today = new Date().toISOString().split('T')[0];
            const todayBookings = bookings.filter(b => b.date === today && b.status === 'confirmed');
            const occupancyRate = dashboardData.desks?.length > 0
                ? Math.round((todayBookings.length / dashboardData.desks.length) * 100)
                : 0;

            setAnalytics(prev => ({
                ...prev,
                occupiedDesks: todayBookings.length,
                occupancyRate: occupancyRate,
                activeBookings: bookings.filter(b => b.status === 'confirmed').length,
            }));
        } catch (err) {
            console.error('Failed to fetch bookings:', err);
        }
    };

    const fetchAvailableDesks = useCallback(async (date) => {
        try {
            console.log('Fetching available desks for date:', date);
            const response = await axios.post('/api/unit-admin/available-desks', { date });
            console.log('Available desks response:', response.data);
            setAvailableDesks(response.data || []);
            if (!response.data || response.data.length === 0) {
                console.warn('No desks available for date:', date);
            }
        } catch (err) {
            console.error('Failed to fetch available desks:', err);
            setError(`Failed to fetch desks: ${err.response?.data?.error || err.message}`);
            setAvailableDesks([]);
        }
    }, []);

    useEffect(() => {
        // Fetch available desks for the selected date
        if (bookingDate) {
            fetchAvailableDesks(bookingDate);
        }
    }, [bookingDate, fetchAvailableDesks]);

    const handleBookDesk = async (e) => {
        e.preventDefault();
        if (!selectedUser || !selectedDeskToBook || !bookingDate) {
            setError('Please fill all fields');
            return;
        }

        try {
            // Find user ID from selected user email
            const user = dashboardData.users.find(u => u.email === selectedUser);
            if (!user) {
                setError('User not found');
                return;
            }

            await axios.post('/api/unit-admin/book-desk', {
                userId: user.id,
                deskId: selectedDeskToBook,
                date: bookingDate,
            });
            setSuccess('Desk booked successfully');
            setSelectedUser('');
            setSelectedDeskToBook('');
            setBookingDate(new Date().toISOString().split('T')[0]);
            fetchBookings();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to book desk');
        }
    };

    const handleCancelBooking = async () => {
        if (!selectedBooking) return;
        try {
            await axios.delete(`/api/unit-admin/bookings/${selectedBooking.id}`);
            setSuccess('Booking cancelled');
            setShowBookingModal(false);
            setSelectedBooking(null);
            fetchBookings();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to cancel booking');
        }
    };

    const handleReassignBooking = async () => {
        if (!selectedBooking || !reassignDeskId) {
            setError('Please select a desk');
            return;
        }
        try {
            await axios.put(`/api/unit-admin/bookings/${selectedBooking.id}/reassign`, {
                newDeskId: reassignDeskId,
            });
            setSuccess('Booking reassigned');
            setShowBookingModal(false);
            setSelectedBooking(null);
            setReassignDeskId('');
            fetchBookings();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to reassign booking');
        }
    };

    const handleReassignDesk = async () => {
        if (!selectedDesk) return;
        try {
            await axios.put(`/api/unit-admin/desks/${selectedDesk.id}/reassign`, {
                action: deskAction,
            });
            setSuccess('Desk updated');
            setShowDeskModal(false);
            setSelectedDesk(null);
            fetchDashboardData();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update desk');
        }
    };

    if (loading) return <div className="loading">Loading unit admin dashboard...</div>;

    return (
        <div className="unit-admin-page">
            <HeaderBar user={user} onLogout={onLogout} />

            <div className="unit-admin-container">
                <div className="unit-admin-header">
                    <div className="header-content">
                        <h1>📊 Unit Admin Dashboard</h1>
                        <p className="unit-name">{dashboardData.unit}</p>
                    </div>
                </div>

                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        📈 Dashboard
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'book-desk' ? 'active' : ''}`}
                        onClick={() => setActiveTab('book-desk')}
                    >
                        📅 Book Desk
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'manage-bookings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manage-bookings')}
                    >
                        ✏️ Manage Bookings
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'manage-desks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manage-desks')}
                    >
                        🔧 Manage Desks
                    </button>
                </div>

                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="tab-content dashboard-tab">
                        <div className="analytics-grid">
                            <div className="analytics-card total-desks">
                                <div className="card-icon">🪑</div>
                                <div className="card-content">
                                    <div className="card-label">Total Desks</div>
                                    <div className="card-value">{analytics.totalDesks}</div>
                                </div>
                            </div>

                            <div className="analytics-card occupancy">
                                <div className="card-icon">📊</div>
                                <div className="card-content">
                                    <div className="card-label">Occupancy Today</div>
                                    <div className="card-value">{analytics.occupancyRate}%</div>
                                    <div className="card-detail">{analytics.occupiedDesks}/{analytics.totalDesks}</div>
                                </div>
                            </div>

                            <div className="analytics-card users">
                                <div className="card-icon">👥</div>
                                <div className="card-content">
                                    <div className="card-label">Unit Users</div>
                                    <div className="card-value">{analytics.totalUsers}</div>
                                </div>
                            </div>

                            <div className="analytics-card bookings">
                                <div className="card-icon">📋</div>
                                <div className="card-content">
                                    <div className="card-label">Active Bookings</div>
                                    <div className="card-value">{analytics.activeBookings}</div>
                                </div>
                            </div>
                        </div>

                        <div className="dashboard-sections">
                            {/* Users Section */}
                            <div className="section">
                                <h3>👥 Unit Users ({dashboardData.users?.length || 0})</h3>
                                <div className="users-grid">
                                    {dashboardData.users?.map((u, idx) => (
                                        <div key={idx} className="user-card">
                                            <div className="user-email">{u.email}</div>
                                            <div className="user-name">{u.name || 'N/A'}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Desks Section */}
                            <div className="section">
                                <h3>🪑 Unit Desks ({dashboardData.desks?.length || 0})</h3>
                                <div className="desks-grid">
                                    {dashboardData.desks?.map((desk, idx) => (
                                        <div key={idx} className="desk-card">
                                            <div className="desk-id">{desk.id}</div>
                                            <div className="desk-zone">{desk.zone}</div>
                                            <div className="desk-monitor">{desk.monitor_setup || 'N/A'}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Book Desk Tab */}
                {activeTab === 'book-desk' && (
                    <div className="tab-content book-desk-tab">
                        <div className="form-card">
                            <h3>📅 Book a Desk for User</h3>
                            <form onSubmit={handleBookDesk}>
                                <div className="form-group">
                                    <label>Select User</label>
                                    <select
                                        value={selectedUser}
                                        onChange={(e) => setSelectedUser(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Choose User --</option>
                                        {dashboardData.users?.map((u, idx) => (
                                            <option key={idx} value={u.email}>
                                                {u.name || u.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Select Date</label>
                                    <input
                                        type="date"
                                        value={bookingDate}
                                        onChange={(e) => setBookingDate(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Select Desk</label>
                                    <select
                                        value={selectedDeskToBook}
                                        onChange={(e) => setSelectedDeskToBook(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Available Desks --</option>
                                        {availableDesks.map((desk, idx) => (
                                            <option key={idx} value={desk.id}>
                                                {desk.id} - {desk.zone}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button type="submit" className="btn-primary">
                                    ✓ Book Desk
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Manage Bookings Tab */}
                {activeTab === 'manage-bookings' && (
                    <div className="tab-content manage-bookings-tab">
                        <h3>✏️ Manage Unit Bookings</h3>
                        <div className="bookings-table-container">
                            <table className="bookings-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Desk</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookingsList?.filter(b => b.status === 'confirmed').map((booking, idx) => (
                                        <tr key={idx}>
                                            <td>{booking.user_email}</td>
                                            <td>{booking.desk_zone}</td>
                                            <td>{booking.date}</td>
                                            <td><span className="status-badge confirmed">{booking.status}</span></td>
                                            <td>
                                                <button
                                                    className="btn-small btn-edit"
                                                    onClick={() => {
                                                        setSelectedBooking(booking);
                                                        setShowBookingModal(true);
                                                    }}
                                                >
                                                    Manage
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Booking Management Modal */}
                        {showBookingModal && selectedBooking && (
                            <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
                                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                    <h3>Manage Booking</h3>
                                    <div className="booking-info">
                                        <p><strong>User:</strong> {selectedBooking.user_email}</p>
                                        <p><strong>Desk:</strong> {selectedBooking.desk_id}</p>
                                        <p><strong>Date:</strong> {selectedBooking.booking_date}</p>
                                    </div>

                                    <div className="modal-tabs">
                                        <button
                                            className={`modal-tab ${bookingAction === 'reassign' ? 'active' : ''}`}
                                            onClick={() => setBookingAction('reassign')}
                                        >
                                            Reassign Desk
                                        </button>
                                        <button
                                            className={`modal-tab ${bookingAction === 'cancel' ? 'active' : ''}`}
                                            onClick={() => setBookingAction('cancel')}
                                        >
                                            Cancel Booking
                                        </button>
                                    </div>

                                    {bookingAction === 'reassign' && (
                                        <div className="modal-form">
                                            <select
                                                value={reassignDeskId}
                                                onChange={(e) => setReassignDeskId(e.target.value)}
                                            >
                                                <option value="">-- Select New Desk --</option>
                                                {dashboardData.desks?.map((desk, idx) => (
                                                    <option key={idx} value={desk.id}>
                                                        {desk.id} - {desk.zone}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                className="btn-primary"
                                                onClick={handleReassignBooking}
                                            >
                                                ✓ Reassign
                                            </button>
                                        </div>
                                    )}

                                    {bookingAction === 'cancel' && (
                                        <div className="modal-form">
                                            <p style={{ color: '#d9534f' }}>Are you sure you want to cancel this booking?</p>
                                            <button
                                                className="btn-danger"
                                                onClick={handleCancelBooking}
                                            >
                                                ✗ Cancel Booking
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        className="btn-secondary"
                                        onClick={() => setShowBookingModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Manage Desks Tab */}
                {activeTab === 'manage-desks' && (
                    <div className="tab-content manage-desks-tab">
                        <h3>🔧 Manage Unit Desks</h3>
                        <div className="desks-management-grid">
                            {deskList.map((desk, idx) => (
                                <div key={idx} className="desk-management-card">
                                    <div className="desk-header">
                                        <h4>{desk.id}</h4>
                                        <button
                                            className="btn-small btn-edit"
                                            onClick={() => {
                                                setSelectedDesk(desk);
                                                setShowDeskModal(true);
                                            }}
                                        >
                                            ⚙️
                                        </button>
                                    </div>
                                    <div className="desk-details">
                                        <p><strong>Zone:</strong> {desk.zone}</p>
                                        <p><strong>Monitor:</strong> {desk.monitor_setup || 'N/A'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desk Management Modal */}
                        {showDeskModal && selectedDesk && (
                            <div className="modal-overlay" onClick={() => setShowDeskModal(false)}>
                                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                    <h3>Manage Desk: {selectedDesk.id}</h3>
                                    <div className="desk-info">
                                        <p><strong>Zone:</strong> {selectedDesk.zone}</p>
                                        <p><strong>Monitor:</strong> {selectedDesk.monitor_setup || 'N/A'}</p>
                                    </div>

                                    <div className="modal-actions">
                                        <button
                                            className="btn-secondary"
                                            onClick={() => {
                                                setDeskAction('flex-pool');
                                                handleReassignDesk();
                                            }}
                                        >
                                            Move to Flex Pool
                                        </button>
                                        <button
                                            className="btn-danger"
                                            onClick={() => {
                                                setDeskAction('delete');
                                                handleReassignDesk();
                                            }}
                                        >
                                            Remove Desk
                                        </button>
                                    </div>

                                    <button
                                        className="btn-secondary"
                                        onClick={() => setShowDeskModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default UnitAdminPage;
